import {
  CircularProgress,
  InputAdornment,
  MenuItem,
  Tooltip,
} from '@material-ui/core';
import { Info } from '@material-ui/icons';
import { FormikCurrencyField, FormikTextField } from '@superdispatch/forms';
import { formatPaymentTerm, PaymentMethod } from '@superdispatch/sdk';
import {
  Column,
  Columns,
  Inline,
  Stack,
  useSnackbarStack,
} from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { getIn, useFormikContext } from 'formik';
import { useEffect, useMemo, useRef } from 'react';
import { ONE_SECOND } from 'shared/constants/NumberConstants';
import {
  CARRIER_PAYMENT_METHODS,
  formatCarrierPaymentMethod,
  listCarrierPaymentTerms,
} from 'shared/helpers/CarrierPaymentTypes';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import { scheduleTimeout } from 'shared/helpers/ReactHelpers';
import { AddLoadDTO } from '../data/LoadDTO';
import { useLoadsContext } from '../data/LoadsContext';

interface TooltipWrapperProps {
  title: string;
  children: JSX.Element;
}

function TooltipWrapper({ title, children }: TooltipWrapperProps) {
  if (!title) return children;

  return (
    <Tooltip title={title}>
      <Box>{children}</Box>
    </Tooltip>
  );
}

interface PaymentAgreementFormProps {
  name?: string;
  vehiclesTotalPrice?: number;
  canPaymentBeEdited?: boolean;
  isEnabledEditLoad?: boolean;
  isReceivedViaSubhaul?: boolean | null;
}

export function PaymentAgreementForm({
  name,
  vehiclesTotalPrice,
  canPaymentBeEdited = true,
  isEnabledEditLoad = true,
  isReceivedViaSubhaul = false,
}: PaymentAgreementFormProps) {
  const prefix = !name ? '' : `${name}.`;
  const { addSnackbar } = useSnackbarStack();
  const {
    loadPriceCalculationStatus: { setIsCalculating, isCalculating },
  } = useLoadsContext();

  const { values, setFieldValue, setFormikState } =
    useFormikContext<AddLoadDTO>();

  const paymentMethod = getIn(
    values,
    `${prefix}payment_details.payment_method`,
  ) as PaymentMethod;

  const paymentMethods = useMemo(() => {
    // Shipper can only use superpay
    if (paymentMethod !== 'superpay') {
      return CARRIER_PAYMENT_METHODS.filter((type) => type !== 'superpay');
    }

    return CARRIER_PAYMENT_METHODS;
  }, [paymentMethod]);

  const priceFieldName = `${prefix}price`;
  const priceFieldValue = (getIn(values, priceFieldName) as number) || 0;
  const previousTotalPriceRef = useRef<number>();

  useEffect(() => {
    // Reset when `vehiclesTotalPrice` is not provided.
    if (vehiclesTotalPrice == null) {
      previousTotalPriceRef.current = undefined;
      return;
    }

    if (previousTotalPriceRef.current == null) {
      previousTotalPriceRef.current = vehiclesTotalPrice;
      return;
    }
    // Skip when `vehiclesTotalPrice` is not changed.
    if (vehiclesTotalPrice === previousTotalPriceRef.current) return;

    setIsCalculating(true);

    return scheduleTimeout(() => {
      setIsCalculating(false);
      const shouldNotify = priceFieldValue !== previousTotalPriceRef.current;

      previousTotalPriceRef.current = vehiclesTotalPrice;

      if (priceFieldValue !== vehiclesTotalPrice) {
        void setFieldValue(priceFieldName, vehiclesTotalPrice || null, false);

        if (shouldNotify) {
          addSnackbar(
            `Payment price adjusted to: ${formatCurrency(vehiclesTotalPrice)}`,
            {
              key: 'load_payment_price_adjust',
            },
          );
        }
      }
    }, ONE_SECOND);
  }, [
    addSnackbar,
    setFieldValue,
    setFormikState,
    setIsCalculating,
    priceFieldName,
    priceFieldValue,
    vehiclesTotalPrice,
  ]);

  return (
    <Stack space="small">
      <TooltipWrapper
        title={
          isEnabledEditLoad
            ? ''
            : 'You cannot change Price for the loads coming from Shipper'
        }
      >
        <FormikCurrencyField
          fullWidth={true}
          label="Price"
          disabled={isCalculating || !canPaymentBeEdited || !isEnabledEditLoad}
          name={`${prefix}price`}
          helperText={
            !!vehiclesTotalPrice && (
              <>Vehicles total price is {formatCurrency(vehiclesTotalPrice)}</>
            )
          }
          inputProps={{ disableValueParsing: true }}
          InputProps={{
            endAdornment: isCalculating ? (
              <InputAdornment position="end">
                <CircularProgress size={12} color="inherit" />
              </InputAdornment>
            ) : undefined,
          }}
        />
      </TooltipWrapper>
      <Columns space="small" collapseBelow="desktop">
        {!isReceivedViaSubhaul && (
          <Column width="1/2">
            <TooltipWrapper
              title={
                isEnabledEditLoad
                  ? ''
                  : 'You cannot change Broker Fee for the loads coming from Shipper'
              }
            >
              <FormikCurrencyField
                fullWidth={true}
                name={`${prefix}broker_fee`}
                disabled={!isEnabledEditLoad}
                label={
                  <Inline noWrap={true} space="xsmall" verticalAlign="center">
                    <span>Broker Fee</span>

                    <Tooltip
                      placement="top"
                      enterTouchDelay={0}
                      title="Fee paid to broker"
                    >
                      <Info fontSize="small" color="action" />
                    </Tooltip>
                  </Inline>
                }
              />
            </TooltipWrapper>
          </Column>
        )}

        <Column width={isReceivedViaSubhaul ? 'fluid' : '1/2'}>
          <FormikCurrencyField
            fullWidth={true}
            name={`${prefix}driver_pay`}
            label={
              <Inline noWrap={true} space="xsmall" verticalAlign="center">
                <span>Driver Pay</span>

                <Tooltip
                  placement="top"
                  enterTouchDelay={0}
                  title="Money that the driver will get for the load. The driver will see this amount in the mobile application."
                >
                  <Info fontSize="small" color="action" />
                </Tooltip>
              </Inline>
            }
          />
        </Column>
      </Columns>

      <Columns space="small" collapseBelow="desktop">
        <Column width="1/2">
          <TooltipWrapper
            title={
              isEnabledEditLoad
                ? ''
                : 'You cannot change Method for the loads coming from Shipper'
            }
          >
            <FormikTextField
              select={true}
              fullWidth={true}
              label="Method"
              disabled={!canPaymentBeEdited || !isEnabledEditLoad}
              name={`${prefix}payment_details.payment_method`}
              onChange={() => {
                void setFieldValue(
                  `${prefix}payment_details.payment_terms`,
                  null,
                  false,
                );
              }}
            >
              {paymentMethods.map((key) => (
                <MenuItem key={key} value={key}>
                  {formatCarrierPaymentMethod(key)}
                </MenuItem>
              ))}
            </FormikTextField>
          </TooltipWrapper>
        </Column>

        <Column width="1/2">
          <TooltipWrapper
            title={
              isEnabledEditLoad
                ? ''
                : 'You cannot change Terms for the loads coming from Shipper'
            }
          >
            <FormikTextField
              select={true}
              fullWidth={true}
              label="Terms"
              disabled={
                !canPaymentBeEdited || !isEnabledEditLoad || !paymentMethod
              }
              name={`${prefix}payment_details.payment_terms`}
            >
              {listCarrierPaymentTerms(paymentMethod).map((key) => {
                return (
                  <MenuItem key={key} value={key}>
                    {formatPaymentTerm(key)}
                  </MenuItem>
                );
              })}
            </FormikTextField>
          </TooltipWrapper>
        </Column>
      </Columns>

      <FormikTextField
        label="Notes"
        multiline={true}
        fullWidth={true}
        name={`${prefix}notes`}
      />
    </Stack>
  );
}
